import { useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import CavelaDirectQuoteSummary from 'src/features/product/product-detail/ui/quotes-tab/CavelaDirectQuoteSummary';
import RfqDrawerTrigger from 'src/features/rfq-builder/ui/rfq-drawer/RfqDrawerTrigger';
import { useMediaQuery } from 'src/shared/hooks';
import type { ProductById } from 'src/shared/types/product';
import type { OrderQuote } from 'src/shared/types/quote';

import { cn } from 'src/shared/components/ui';

import CavelaDirectRfqSlideOver, {
  CavelaDirectRfqSlideOverTab
} from './CavelaDirectRfqSlideOver';

/** Total intro sequence length (ms); loader fades in, holds, then fades out. */
const INTRO_DURATION_MS = 3000;

/** Blue grid reveal overlay — must stay in sync with `transition` on the overlay `motion.div`. */
const REVEAL_OVERLAY_DELAY_S = 0;
const REVEAL_OVERLAY_DURATION_S = 1.2;

/** Same timing as {@link RfqPlayground} + {@link CavelaDirectRfqSlideOver} drawer. */
const QUOTE_PANE_TRANSITION = { duration: 0.3, ease: 'easeInOut' as const };

interface CavelaDirectPageProps {
  product?: ProductById | null;
  /** Real order quotes */
  orderQuotes?: OrderQuote[];
  /** When set (e.g. PDP `?animate` flow), "Not for now" removes `animate` so the full product page reveal can run. */
  onDismissAnimate?: () => void;
  /** Called after the Purchase button succeeds and the Stripe tab opens. Defaults to onDismissAnimate if not provided. */
  onPurchaseComplete?: () => void;
}

/** Centered placeholder rect shown before the real card is measured. */
const getDefaultCardRect = () => {
  const width = Math.min(480, window.innerWidth - 40);
  const height = 320;

  return {
    top: 200,
    left: (window.innerWidth - width) / 2,
    width,
    height
  };
};

const CavelaDirectPage: React.FC<CavelaDirectPageProps> = ({
  product,
  orderQuotes,
  onDismissAnimate,
  onPurchaseComplete
}) => {
  const [introDone, setIntroDone] = useState(false);
  /** Full-screen blue grid stays in the tree from load until the wipe + delay finish. */
  const [revealOverlayInDom, setRevealOverlayInDom] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardRect, setCardRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  }>(getDefaultCardRect);
  const [timerDone, setTimerDone] = useState(false);
  const [showCreatingText, setShowCreatingText] = useState(true);
  const [isRfqDrawerOpen, setIsRfqDrawerOpen] = useState(false);
  const [rfqDrawerTab, setRfqDrawerTab] =
    useState<CavelaDirectRfqSlideOverTab>('rfq');
  const isMobile = useMediaQuery();

  const hasRfqDrawer = Boolean(product?.rfq);
  /** Desktop only: quote shifts left when RFQ panel is open so both fit. */
  const splitQuoteForOpenDrawer =
    hasRfqDrawer && isRfqDrawerOpen && !(isMobile ?? false);

  useEffect(() => {
    if (cardRef.current) {
      const r = cardRef.current.getBoundingClientRect();

      setCardRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height
      });
    }
  }, [product, introDone, splitQuoteForOpenDrawer, isMobile]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setTimerDone(true);
    }, INTRO_DURATION_MS);

    return () => {
      window.clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setShowCreatingText(false);
    }, 3500);

    return () => {
      window.clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    if (
      timerDone &&
      product &&
      product.auto_quote_result &&
      orderQuotes &&
      orderQuotes.length > 0
    ) {
      setIntroDone(true);
    }
  }, [timerDone, product, orderQuotes]);

  useEffect(() => {
    if (!introDone) {
      return;
    }
    const wipeMs = (REVEAL_OVERLAY_DELAY_S + REVEAL_OVERLAY_DURATION_S) * 1000;
    const id = window.setTimeout(() => {
      setRevealOverlayInDom(false);
    }, wipeMs);

    return () => {
      window.clearTimeout(id);
    };
  }, [introDone]);

  return (
    <div className="fixed inset-0 z-50 h-dvh w-full overflow-hidden">
      {/* gray grid */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `
              linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
            `,
          backgroundSize: '40px 40px',
          backgroundColor: '#FFF'
        }}
      />
      {/*
        Keep quote UI below intro overlays (z-30 / z-40). A higher z (e.g. z-60) stacks above
        the blue clip-path wipe and hides the initial animation.
      */}
      <motion.div
        className="relative z-10 flex h-dvh min-h-0 w-full flex-col"
        initial={false}
        animate={{ opacity: introDone ? 1 : 0 }}
        transition={QUOTE_PANE_TRANSITION}
        style={{
          pointerEvents: introDone ? 'auto' : 'none'
        }}
        aria-hidden={!introDone}
      >
        {/*
          Match RfqPlayground: fixed left pane animates width 100% <-> 50%; inner column stays
          horizontally centered in that pane (left 50% + -translate-x-1/2) so the quote
          slides with the pane instead of flex “teleporting.”
        */}
        <div className="relative flex h-dvh min-h-0 w-full">
          {product?.rfq && !isRfqDrawerOpen ? (
            <RfqDrawerTrigger
              onClick={() => {
                setRfqDrawerTab('rfq');
                setIsRfqDrawerOpen(true);
              }}
              isDrawerOpen={false}
              className="z-[66]"
            />
          ) : null}
          <motion.div
            initial={false}
            animate={{
              left: 0,
              width: splitQuoteForOpenDrawer
                ? (isMobile ?? false)
                  ? '100%'
                  : '50%'
                : '100%',
              right: 0,
              opacity: 1
            }}
            transition={QUOTE_PANE_TRANSITION}
            className="fixed top-0 bottom-0 right-0 left-0 z-10 flex min-h-0 w-full flex-col overflow-y-auto cavela-scrollbar bg-white h-dvh"
            style={{
              backgroundImage: splitQuoteForOpenDrawer
                ? 'none'
                : `
              linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
            `,
              backgroundSize: '40px 40px',
              backgroundColor: '#FFF'
            }}
          >
            {/*
              Keep the quote column at w-full max-w-2xl (no animated maxWidth). Animating
              percentage maxWidth while the pane width tweens 50%→100% re-resolves % against
              a growing parent and the card briefly fills the viewport before snapping down
              (“big to small”). Width now tracks the pane via min(100%, 42rem) only.
            */}
            {product && (
              <div className="absolute left-1/2 top-0 flex h-full min-h-0 w-full -translate-x-1/2 flex-col gap-6 px-5 pb-10 mb-10 pt-8 md:px-8">
                <h1
                  className={cn(
                    'w-full shrink-0 text-center font-bold leading-tight text-black transition-all duration-300',
                    splitQuoteForOpenDrawer
                      ? 'text-5xl mobile:text-[36px] mt-2'
                      : 'text-[56px] mobile:text-[40px] mobile:mt-0 mt-10'
                  )}
                >
                  Your quote is ready
                </h1>

                <div
                  className={cn('flex min-h-0 flex-1 flex-col justify-start')}
                >
                  <div className="mobile:pb-10">
                    {product?.auto_quote_result && (
                      <CavelaDirectQuoteSummary
                        cardRef={cardRef}
                        product={product}
                        orderQuotes={orderQuotes}
                        onDismissAnimate={onDismissAnimate}
                        onPurchaseComplete={
                          onPurchaseComplete ?? onDismissAnimate
                        }
                        onOpenRfqDrawer={(tab) => {
                          setRfqDrawerTab(tab);
                          setIsRfqDrawerOpen(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {product?.rfq ? (
            <CavelaDirectRfqSlideOver
              rfq={product.rfq}
              isDrawerOpen={isRfqDrawerOpen}
              onToggle={() => setIsRfqDrawerOpen((open) => !open)}
              isMobile={isMobile ?? false}
              activeTab={rfqDrawerTab}
              onActiveTabChange={setRfqDrawerTab}
              drawerPanelClassName="z-[65]"
              triggerClassName="z-[70]"
            />
          ) : null}
        </div>
      </motion.div>

      {revealOverlayInDom && (
        <motion.div
          key="reveal-overlay"
          className="absolute inset-0 z-30 pointer-events-none"
          initial={{ clipPath: 'inset(0% 0 0% 0)' }}
          animate={{
            clipPath: introDone ? 'inset(0% 0 100% 0)' : 'inset(0% 0 0% 0)'
          }}
          transition={
            introDone
              ? {
                  duration: REVEAL_OVERLAY_DURATION_S,
                  ease: 'easeInOut',
                  delay: REVEAL_OVERLAY_DELAY_S
                }
              : { duration: 0 }
          }
          style={{
            backgroundImage: `
                linear-gradient(to right, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
              `,
            backgroundSize: '40px 40px',
            backgroundColor: '#000EEF'
          }}
        />
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showCreatingText ? 1 : 0 }}
        transition={{
          duration: showCreatingText ? 0.8 : 0.6,
          ease: 'easeInOut'
        }}
        className="absolute inset-0 z-[30] pointer-events-none pt-8"
      >
        <h1 className="text-center text-[56px] mobile:text-[40px] font-bold text-white mt-10 mobile:mt-0">
          Creating your quote
        </h1>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: revealOverlayInDom ? 0.6 : 0 }}
        transition={{
          duration: revealOverlayInDom ? 0.8 : 1,
          ease: 'easeInOut'
        }}
        className="absolute inset-0 z-40 pointer-events-none"
      >
        {/*
          Outer div animates position/size from the default placeholder rect to the real
          card rect once the product is available. Scale pulse lives in a nested div so
          updating cardRect doesn't restart the infinite loop.
        */}
        <motion.div
          className="absolute rounded-2xl overflow-hidden"
          initial={false}
          animate={{
            top: cardRect.top,
            left: cardRect.left,
            width: cardRect.width,
            height: cardRect.height
          }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
          <motion.div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent 0 3px, rgba(0,0,0,0.2) 3px 4px),
                repeating-linear-gradient(-45deg, transparent 0 3px, rgba(0,0,0,0.2) 3px 4px),
                radial-gradient(ellipse 130% 130% at 20% 45%, rgba(0,14,239,0.35), rgba(0,14,239,0.08) 45%, transparent 75%)
              `,
              backgroundColor: 'rgba(255,255,255,0.98)'
            }}
            animate={{ scale: [1, 1.02, 1.01, 1.02, 1] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: [0.4, 0, 0.2, 1]
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CavelaDirectPage;
